import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Chart from "react-apexcharts";
import {
  IoPerson,
  IoTime,
  IoRefresh,
  IoEllipseSharp,
  IoChevronDown,
  IoChevronUp,
  IoCalendar,
  IoBarChart,
  IoTrendingUp,
} from "react-icons/io5";

import GlassCard from "components/GlassCard";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiBadge from "components/ui/VuiBadge";
import VuiButton from "components/ui/VuiButton";

import { adminService } from "services/adminService";

// ── Constants ──────────────────────────────────────────────────────────────

const MODULE_LABELS = {
  AI_REPORTS: "Competitor Reports",
  MARKET_OVERVIEW: "Content Strategy",
  COMPETITOR_INSIGHTS: "Digital Presence",
  PRESS_COVERAGE: "Press Digest",
  PRODUCT_INSIGHTS: "Product Trends",
  EXPANSION_TRENDS: "Expansion Trends",
  RD_LANDSCAPE: "R&D Landscape",
  PRODUCT_SHOWDOWN: "Product Showdown",
  PRICING_ANALYTICS: "Pricing Analytics",
};

const DOW_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── Helpers ────────────────────────────────────────────────────────────────

function friendlyModule(raw) {
  return MODULE_LABELS[raw] || raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function activeDot(lastSeen) {
  if (!lastSeen) return "none";
  const days = (Date.now() - new Date(lastSeen).getTime()) / 86400000;
  if (days < 1) return "success";
  if (days < 7) return "warning";
  return "inactive";
}

function dotColor(dot) {
  if (dot === "success") return "#4ade80";
  if (dot === "warning") return "#facc15";
  return "#4b5563";
}

// ── Chart helpers ──────────────────────────────────────────────────────────

function buildDailyChartData(dailyCounts) {
  // Last 90 days, fill missing days with 0
  const result = [];
  const today = new Date();
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().split("T")[0];
    result.push({ x: key, y: dailyCounts[key] ?? 0 });
  }
  return result;
}

function buildDowChartData(dowCounts) {
  return DOW_ORDER.map((day) => ({
    x: day,
    y: dowCounts[day] ?? 0,
  }));
}

function buildHourChartData(hourCounts) {
  return Array.from({ length: 24 }, (_, h) => ({
    x: `${String(h).padStart(2, "0")}:00`,
    y: hourCounts[String(h)] ?? 0,
  }));
}

function buildMonthlyData(dailyCounts) {
  const monthly = {};
  Object.entries(dailyCounts).forEach(([date, count]) => {
    const month = date.slice(0, 7); // "2026-03"
    monthly[month] = (monthly[month] ?? 0) + count;
  });
  return Object.entries(monthly)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, count]) => ({
      month: new Date(month + "-01").toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
      count,
    }));
}

const CHART_OPTS_BASE = {
  chart: { toolbar: { show: false }, background: "transparent", sparkline: { enabled: false } },
  grid: { borderColor: "rgba(255,255,255,0.06)", strokeDashArray: 3 },
  tooltip: { theme: "dark" },
  xaxis: { labels: { style: { colors: "rgba(255,255,255,0.4)", fontSize: "10px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { colors: "rgba(255,255,255,0.4)", fontSize: "10px" } } },
  fill: { opacity: 1 },
};

// ── Sub-components ─────────────────────────────────────────────────────────

function StatLine({ label, value }) {
  return (
    <VuiBox display="flex" justifyContent="space-between" alignItems="center" py={0.25}>
      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem" }}>
        {label}
      </VuiTypography>
      <VuiTypography variant="caption" color="white" sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
        {value}
      </VuiTypography>
    </VuiBox>
  );
}

function SectionLabel({ children }) {
  return (
    <VuiTypography variant="caption" sx={{
      color: "rgba(255,255,255,0.4)",
      fontSize: "0.68rem",
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      display: "block",
      mb: 1,
    }}>
      {children}
    </VuiTypography>
  );
}

// ── User Row ───────────────────────────────────────────────────────────────

function UserRow({ user, companyNameMap }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dot = activeDot(user.lastSeen);
  const hasActivity = user.totalEvents > 0;

  const loadDetail = useCallback(async () => {
    if (data || !hasActivity) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, sessionsRes, timelineRes] = await Promise.all([
        adminService.getUserActivitySummary(user._id),
        adminService.getUserSessions(user._id, 5),
        adminService.getUserTimeline(user._id),
      ]);
      setData({
        summary: summaryRes.data,
        sessions: sessionsRes.data,
        timeline: timelineRes.data,
      });
    } catch {
      setError("Failed to load detail.");
    } finally {
      setLoading(false);
    }
  }, [user._id, hasActivity, data]);

  const handleToggle = () => {
    setOpen((p) => !p);
    if (!open) loadDetail();
  };

  // ── Derived chart data ─────────────────────────────────────────────────
  const dailySeries = data ? buildDailyChartData(data.timeline?.dailyCounts ?? {}) : [];
  const dowSeries = data ? buildDowChartData(data.timeline?.dayOfWeekCounts ?? {}) : [];
  const hourSeries = data ? buildHourChartData(data.timeline?.hourOfDayCounts ?? {}) : [];
  const monthlyData = data ? buildMonthlyData(data.timeline?.dailyCounts ?? {}) : [];
  const moduleUsage = data ? Object.entries(data.summary?.moduleUsage ?? {}).sort((a, b) => b[1] - a[1]) : [];
  const topCompetitors = data?.summary?.topCompetitors ?? [];
  const sessions = data?.sessions ?? [];

  // Most active day of week
  const peakDow = data
    ? DOW_ORDER.reduce((best, day) => {
        const count = data.timeline?.dayOfWeekCounts?.[day] ?? 0;
        const bestCount = data.timeline?.dayOfWeekCounts?.[best] ?? 0;
        return count > bestCount ? day : best;
      }, "Mon")
    : null;

  // Peak hour
  const peakHour = data
    ? Object.entries(data.timeline?.hourOfDayCounts ?? {}).sort((a, b) => b[1] - a[1])[0]?.[0]
    : null;

  return (
    <GlassCard sx={{ mb: 1.5 }}>
      {/* ── Header row ────────────────────────────────────────────────── */}
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1.5}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={handleToggle}
      >
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <IoEllipseSharp size="10px" color={dotColor(dot)} />
          <VuiBox>
            <VuiTypography variant="button" color="white" fontWeight="medium">
              {user.username || "Unknown"}
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem", display: "block" }}>
              {user.companyName || "—"} · {user.email || ""}
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={3}>
          <VuiBox textAlign="right">
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Last seen</VuiTypography>
            <VuiTypography variant="caption" color="white" sx={{ display: "block", fontSize: "0.73rem" }}>
              {timeAgo(user.lastSeen)}
            </VuiTypography>
          </VuiBox>
          <VuiBox textAlign="right">
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Total events</VuiTypography>
            <VuiTypography variant="caption" color="white" sx={{ display: "block", fontSize: "0.73rem" }}>
              {user.totalEvents}
            </VuiTypography>
          </VuiBox>
          {open
            ? <IoChevronUp size="15px" color="rgba(255,255,255,0.4)" />
            : <IoChevronDown size="15px" color="rgba(255,255,255,0.4)" />}
        </VuiBox>
      </VuiBox>

      {/* ── Expanded detail ────────────────────────────────────────────── */}
      <Collapse in={open}>
        <VuiBox px={2} pb={3}>

          {!hasActivity && (
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>
              No activity recorded — user has not navigated any pages in production.
            </VuiTypography>
          )}

          {hasActivity && loading && (
            <VuiBox display="flex" justifyContent="center" py={3}>
              <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.3)" }} />
            </VuiBox>
          )}

          {hasActivity && error && (
            <VuiTypography variant="caption" sx={{ color: "#f87171" }}>{error}</VuiTypography>
          )}

          {hasActivity && data && (
            <VuiBox display="flex" flexDirection="column" gap={3}>

              {/* ── Row 1: Key stats ──────────────────────────────────── */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <VuiBox sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "10px", p: 1.5 }}>
                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Active days</VuiTypography>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">{data.summary?.activeDays ?? "—"}</VuiTypography>
                  </VuiBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <VuiBox sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "10px", p: 1.5 }}>
                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Sessions</VuiTypography>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      {data.summary?.activityPatterns?.totalSessions ?? "—"}
                    </VuiTypography>
                  </VuiBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <VuiBox sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "10px", p: 1.5 }}>
                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Peak day</VuiTypography>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">{peakDow ?? "—"}</VuiTypography>
                  </VuiBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <VuiBox sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "10px", p: 1.5 }}>
                    <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>Peak hour</VuiTypography>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      {peakHour != null ? `${String(peakHour).padStart(2, "0")}:00` : "—"}
                    </VuiTypography>
                  </VuiBox>
                </Grid>
              </Grid>

              {/* ── Row 2: Daily activity chart (90d) ────────────────── */}
              <VuiBox>
                <SectionLabel>Daily Activity — Last 90 Days</SectionLabel>
                <Chart
                  type="bar"
                  height={90}
                  series={[{ name: "Events", data: dailySeries }]}
                  options={{
                    ...CHART_OPTS_BASE,
                    chart: { ...CHART_OPTS_BASE.chart, type: "bar" },
                    plotOptions: { bar: { columnWidth: "80%", borderRadius: 1 } },
                    colors: ["#3b82f6"],
                    xaxis: {
                      ...CHART_OPTS_BASE.xaxis,
                      type: "datetime",
                      tickAmount: 6,
                    },
                    dataLabels: { enabled: false },
                  }}
                />
              </VuiBox>

              {/* ── Row 3: Monthly + Day of week + Hour of day ───────── */}
              <Grid container spacing={2}>

                {/* Monthly connections */}
                <Grid item xs={12} sm={4}>
                  <SectionLabel>Monthly Connections</SectionLabel>
                  <VuiBox display="flex" flexDirection="column" gap={0.5}>
                    {monthlyData.length === 0 && (
                      <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>No data</VuiTypography>
                    )}
                    {monthlyData.map(({ month, count }) => (
                      <VuiBox key={month} display="flex" justifyContent="space-between" alignItems="center">
                        <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.72rem" }}>
                          {month}
                        </VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={1}>
                          <VuiBox
                            sx={{
                              width: `${Math.min(count * 8, 80)}px`,
                              height: "4px",
                              background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                              borderRadius: "2px",
                            }}
                          />
                          <VuiTypography variant="caption" color="white" sx={{ fontSize: "0.72rem", minWidth: "20px", textAlign: "right" }}>
                            {count}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                    ))}
                  </VuiBox>
                </Grid>

                {/* Day of week */}
                <Grid item xs={12} sm={4}>
                  <SectionLabel>Busiest Day of Week</SectionLabel>
                  <Chart
                    type="bar"
                    height={120}
                    series={[{ name: "Events", data: dowSeries }]}
                    options={{
                      ...CHART_OPTS_BASE,
                      chart: { ...CHART_OPTS_BASE.chart, type: "bar" },
                      plotOptions: { bar: { columnWidth: "60%", borderRadius: 2, distributed: true } },
                      colors: DOW_ORDER.map((d) => (d === peakDow ? "#8b5cf6" : "#1e3a5f")),
                      legend: { show: false },
                      dataLabels: { enabled: false },
                      xaxis: { ...CHART_OPTS_BASE.xaxis, categories: DOW_ORDER },
                    }}
                  />
                </Grid>

                {/* Hour of day */}
                <Grid item xs={12} sm={4}>
                  <SectionLabel>Hour of Day (UTC)</SectionLabel>
                  <Chart
                    type="area"
                    height={120}
                    series={[{ name: "Events", data: hourSeries }]}
                    options={{
                      ...CHART_OPTS_BASE,
                      chart: { ...CHART_OPTS_BASE.chart, type: "area" },
                      stroke: { curve: "smooth", width: 2 },
                      colors: ["#06b6d4"],
                      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0, stops: [0, 100] } },
                      dataLabels: { enabled: false },
                      xaxis: {
                        ...CHART_OPTS_BASE.xaxis,
                        tickAmount: 6,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* ── Row 4: Pages used + Top competitors ──────────────── */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SectionLabel>Pages Used</SectionLabel>
                  {moduleUsage.length === 0
                    ? <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>No data</VuiTypography>
                    : moduleUsage.map(([mod, count]) => {
                        const maxCount = moduleUsage[0][1];
                        return (
                          <VuiBox key={mod} display="flex" alignItems="center" gap={1} mb={0.75}>
                            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.72rem", minWidth: "140px" }}>
                              {friendlyModule(mod)}
                            </VuiTypography>
                            <VuiBox
                              sx={{
                                flex: 1,
                                height: "5px",
                                background: "rgba(255,255,255,0.07)",
                                borderRadius: "3px",
                                overflow: "hidden",
                              }}
                            >
                              <VuiBox
                                sx={{
                                  width: `${(count / maxCount) * 100}%`,
                                  height: "100%",
                                  background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
                                  borderRadius: "3px",
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </VuiBox>
                            <VuiTypography variant="caption" color="white" sx={{ fontSize: "0.72rem", minWidth: "24px", textAlign: "right" }}>
                              {count}
                            </VuiTypography>
                          </VuiBox>
                        );
                      })}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SectionLabel>Top Competitors Viewed</SectionLabel>
                  {topCompetitors.length === 0
                    ? <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>No data</VuiTypography>
                    : topCompetitors.map((c, i) => (
                        <VuiBox key={i} display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.72rem" }}>
                            {companyNameMap[c.competitorId] || c.competitorId}
                          </VuiTypography>
                          <VuiBadge variant="standard" badgeContent={String(c.viewCount)} color="secondary" size="xs" container />
                        </VuiBox>
                      ))}
                </Grid>
              </Grid>

              {/* ── Row 5: Session journeys ───────────────────────────── */}
              {sessions.length > 0 && (
                <VuiBox>
                  <SectionLabel>Recent Sessions</SectionLabel>
                  <VuiBox display="flex" flexDirection="column" gap={1}>
                    {sessions.map((s, i) => (
                      <VuiBox
                        key={i}
                        sx={{ background: "rgba(255,255,255,0.03)", borderRadius: "8px", p: 1.25, border: "1px solid rgba(255,255,255,0.06)" }}
                      >
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                          <VuiTypography variant="caption" color="white" sx={{ fontSize: "0.73rem", fontWeight: 500 }}>
                            {s.startTime
                              ? new Date(s.startTime).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </VuiTypography>
                          <VuiBox display="flex" gap={1.5}>
                            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>
                              {s.durationMinutes != null ? `${s.durationMinutes}m` : "< 1m"}
                            </VuiTypography>
                            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem" }}>
                              {s.activityCount} events
                            </VuiTypography>
                          </VuiBox>
                        </VuiBox>
                        <VuiBox display="flex" flexWrap="wrap" gap={0.5}>
                          {s.moduleUsage && Object.entries(s.moduleUsage).map(([mod, count]) => (
                            <VuiTypography
                              key={mod}
                              variant="caption"
                              sx={{
                                fontSize: "0.67rem",
                                color: "rgba(255,255,255,0.55)",
                                background: "rgba(255,255,255,0.06)",
                                borderRadius: "4px",
                                px: 0.75,
                                py: 0.25,
                              }}
                            >
                              {friendlyModule(mod)} ×{count}
                            </VuiTypography>
                          ))}
                        </VuiBox>
                      </VuiBox>
                    ))}
                  </VuiBox>
                </VuiBox>
              )}

            </VuiBox>
          )}
        </VuiBox>
      </Collapse>
    </GlassCard>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function UserActivity() {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyNameMap, setCompanyNameMap] = useState({});

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, activityRes, companiesRes] = await Promise.all([
        adminService.getAllUsers(),
        adminService.getActivityOverview(),
        adminService.getAllCompanies(),
      ]);

      // Build id → name map for competitor display
      const nameMap = {};
      (companiesRes.data ?? []).forEach(c => { nameMap[c.id] = c.name; });
      setCompanyNameMap(nameMap);

      const enabledUsers = (usersRes.data ?? []).filter(
        (u) => u.enabled && !u.email?.toLowerCase().includes("porteriq")
      );

      const activityById = {};
      (activityRes.data ?? []).forEach((a) => { activityById[a._id] = a; });

      const merged = enabledUsers.map((u) => {
        const activity = activityById[u.id] ?? {};
        return {
          _id: u.id,
          username: u.username,
          companyName: u.companyName,
          email: u.email,
          lastSeen: activity.lastSeen ?? null,
          totalEvents: activity.totalEvents ?? 0,
        };
      });

      merged.sort((a, b) => {
        if (a.lastSeen && b.lastSeen) return new Date(b.lastSeen) - new Date(a.lastSeen);
        if (a.lastSeen) return -1;
        if (b.lastSeen) return 1;
        return (a.username ?? "").localeCompare(b.username ?? "");
      });

      setOverview(merged);
    } catch {
      setError("Failed to load user activity.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const activeToday = overview.filter(
    (u) => u.lastSeen && (Date.now() - new Date(u.lastSeen).getTime()) < 86400000
  ).length;

  const activeThisWeek = overview.filter(
    (u) => u.lastSeen && (Date.now() - new Date(u.lastSeen).getTime()) < 7 * 86400000
  ).length;

  return (
    <VuiBox display="flex" flexDirection="column" gap={3}>

      {/* Header */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between">
        <VuiTypography variant="h6" color="white">User Activity</VuiTypography>
        <VuiButton size="small" variant="outlined" color="secondary" onClick={fetchOverview} disabled={loading}
          sx={{ minWidth: "36px", minHeight: "32px", px: 1 }}>
          <IoRefresh size="14px" />
        </VuiButton>
      </VuiBox>

      {error && (
        <VuiTypography variant="caption" sx={{ color: "#f87171" }}>{error}</VuiTypography>
      )}

      {/* Summary cards */}
      {!loading && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <GlassCard>
              <VuiBox display="flex" alignItems="center" gap={1.5} p={0.5}>
                <IoPerson size="20px" color="rgba(255,255,255,0.5)" />
                <VuiBox>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">{overview.length}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>Total users</VuiTypography>
                </VuiBox>
              </VuiBox>
            </GlassCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <GlassCard>
              <VuiBox display="flex" alignItems="center" gap={1.5} p={0.5}>
                <IoEllipseSharp size="12px" color="#4ade80" />
                <VuiBox>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">{activeToday}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>Active today</VuiTypography>
                </VuiBox>
              </VuiBox>
            </GlassCard>
          </Grid>
          <Grid item xs={12} sm={4}>
            <GlassCard>
              <VuiBox display="flex" alignItems="center" gap={1.5} p={0.5}>
                <IoTime size="20px" color="rgba(255,255,255,0.5)" />
                <VuiBox>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">{activeThisWeek}</VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.7rem" }}>Active this week</VuiTypography>
                </VuiBox>
              </VuiBox>
            </GlassCard>
          </Grid>
        </Grid>
      )}

      {/* User list */}
      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress sx={{ color: "rgba(255,255,255,0.3)" }} />
        </VuiBox>
      ) : overview.length === 0 ? (
        <GlassCard>
          <VuiBox p={3} display="flex" justifyContent="center">
            <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>
              No users found.
            </VuiTypography>
          </VuiBox>
        </GlassCard>
      ) : (
        <VuiBox>
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", mb: 1.5, display: "block" }}>
            🟢 Active today &nbsp;·&nbsp; 🟡 Active this week &nbsp;·&nbsp; ⚫ Inactive · Click to expand
          </VuiTypography>
          {overview.map((user) => (
            <UserRow key={user._id} user={user} companyNameMap={companyNameMap} />
          ))}
        </VuiBox>
      )}
    </VuiBox>
  );
}
